import { useState, useEffect, useRef } from 'react';
import { supa } from '../lib/supabase.js';
import { Camera, CameraResultType, CameraSource } from '@capacitor/camera';
import { Capacitor } from '@capacitor/core';
import { TH, MP, THOUGHT_FONTS, REWARDS_CATALOG, INIT_FILTERS } from '../lib/constants.js';
import { isPhotoUrl, compressImage, pushToast, blobToDataUrl, sectionGlowBg } from '../lib/helpers.js';
import { YR, DOY, TODAY } from '../lib/dateState.js';
import { textWritingFlag } from '../lib/gestureFlags.js';
import { uploadPhotoToStorage } from '../lib/api.js';
import { postToSharedBook, fetchSharedBooksPostedToday } from '../lib/api.js';
import { createMicroBook } from '../lib/api.js';
import { t as tr, LOCALE } from '../lib/i18n.js';
import { Btn, Back } from '../components/Basic.jsx';
import { NavIcon } from '../components/Navigation.jsx';
import { FilterTile } from '../components/FilterTile.jsx';
import { ShareSheet } from './Feed.jsx';
// NOTA: FiltersView si collega qui sotto una volta estratta la vista Filtri.
import { FiltersView } from './Filters.jsx';
// Ritaglia l'immagine originale in un quadrato, nel punto scelto dall'utente
// trascinando l'anteprima. offsetXPercent/offsetYPercent vanno da 0 a 100 —
// 50/50 è il centro (comportamento di prima), 0 è tutto a sinistra/in alto,
// 100 tutto a destra/in basso. Il ritaglio avviene qui, non solo a schermo,
// così la foto salvata riflette davvero quello che l'utente ha scelto.
function cropImageToSquare(dataUrl,offsetXPercent=50,offsetYPercent=50,maxDim=1600,quality=0.85){
  return new Promise((resolve,reject)=>{
    const img=new Image();
    img.onload=()=>{
      const w=img.width,h=img.height;
      const side=Math.min(w,h);
      const maxOffsetX=w-side,maxOffsetY=h-side;
      const sx=maxOffsetX*(offsetXPercent/100);
      const sy=maxOffsetY*(offsetYPercent/100);
      const outSize=Math.min(maxDim,side);
      const canvas=document.createElement("canvas");
      canvas.width=outSize; canvas.height=outSize;
      const ctx=canvas.getContext("2d");
      ctx.drawImage(img,sx,sy,side,side,0,0,outSize,outSize);
      canvas.toBlob(blob=>blob?resolve(blob):reject(new Error("ritaglio fallito")),"image/jpeg",quality);
    };
    img.onerror=()=>reject(new Error("immagine non valida"));
    img.src=dataUrl;
  });
}
export function CaptureView({t,st,dispatch,go,userId,capturePreset,clearCapturePreset,onNavVisible}){
  const [step,setStep]=useState(0);
  // La barra in basso (ora un'unica istanza ferma in App.jsx, vedi
  // Navigation.jsx) deve sparire SOLO nel passo di scrittura/modifica dopo
  // lo scatto (step>0) — esattamente come faceva prima quando BNav viveva
  // qui dentro. App.jsx non ha modo di saperlo da solo, quindi glielo
  // segnaliamo noi ad ogni cambio di step.
  useEffect(()=>{ if(onNavVisible) onNavVisible(step===0); },[step]);
  const [photo,setPhoto]=useState("📷");
  const [caption,setCaption]=useState("");
  // Se si arriva qui dal pulsante "Scatta oggi" DENTRO un micro-libro o un
  // libro condiviso (capturePreset valorizzato da App.jsx), si parte già in
  // modalità "solo per quel libro" — niente schermata "hai già scattato
  // oggi", niente inserimento automatico nel diario personale, e il libro
  // di provenienza è già preselezionato. Da qui, un tocco su "Scatta" del
  // tab principale in basso resetta sempre capturePreset a null in App.jsx,
  // quindi questi valori iniziali sono validi solo per QUESTO ingresso.
  const [saveMicroId,setSaveMicroId]=useState(()=>(capturePreset&&capturePreset.microId)||null); // optional, in addition to the annual book (always included)
  const [saveSharedId,setSaveSharedId]=useState(()=>(capturePreset&&capturePreset.sharedId)||null); // optional, a shared/family book — independent of saveMicroId
  const [filterId,setFilterId]=useState("none");
  const [stickerId,setStickerId]=useState(null);
  const [showNewMicro,setShowNewMicro]=useState(false);
  const [newMicroTitle,setNewMicroTitle]=useState("");
  const [creatingMicro,setCreatingMicro]=useState(false);
  const [saving,setSaving]=useState(false);
  const [microOnlyMode,setMicroOnlyMode]=useState(()=>!!capturePreset);
  // Solo quando si è entrati da un micro-libro/libro condiviso e il diario
  // di oggi è ancora vuoto: vuoi che QUESTA stessa foto conti anche come
  // foto del giorno nel diario personale? Di default no — il diario resta
  // toccato solo se lo scegli esplicitamente.
  const [addToDiaryToo,setAddToDiaryToo]=useState(false);
  const [blockedInfo,setBlockedInfo]=useState(null);
  const [thoughtFont,setThoughtFont]=useState("inter");
  const [cropX,setCropX]=useState(50);
  const [cropY,setCropY]=useState(50);
  // Libri EVENTO (matrimoni etc.): a differenza del libro annuale, che
  // ammette una sola pagina al giorno, un libro condiviso di tipo "evento"
  // deve poter ricevere tante foto quante ne vuole chi partecipa, tutte
  // nello stesso giorno (un ricevimento dura ore, non un istante). Quando
  // questo è valorizzato, si salta lo schermo "hai già pubblicato oggi" e
  // si torna dritti alla fotocamera con lo stesso libro evento già scelto.
  const [eventLoopId,setEventLoopId]=useState(null);
  const dragState=useRef({active:false,startX:0,startY:0,startCropX:50,startCropY:50});
  useEffect(()=>{
    textWritingFlag.active=(photo==="✏️"&&step===1);
    return ()=>{ textWritingFlag.active=false; };
  },[photo,step]);
  useEffect(()=>{ setCropX(50); setCropY(50); },[photo]); // ogni nuovo scatto riparte centrato
  // Il preset serve solo per inizializzare lo stato di QUESTO montaggio
  // (vedi useState qui sopra) — una volta letto, si scarica subito, così
  // un rientro successivo nel tab "Scatta" normale non lo ritrova ancora lì.
  useEffect(()=>{ if(capturePreset&&clearCapturePreset) clearCapturePreset(); },[]);
  const [justPosted,setJustPosted]=useState(null);
  const [showShareAfter,setShowShareAfter]=useState(false);
  const [showGallery,setShowGallery]=useState(false);
  const todayDone=st.books[YR]?.pages?.some(p=>p.day===DOY);
  const openMicro=st.micro.filter(m=>!m.closed);
  const openShared=(st.sharedBooks||[]).filter(m=>!m.closed);
  // I libri condivisi "family"/"a tempo" ammettono una sola foto al giorno
  // PER UTENTE (vedi commento su postToSharedBook in lib/api.js) — controllato
  // QUI, prima ancora di provare a pubblicare, così se IO ho già pubblicato
  // la mia foto di oggi lo vedo subito nella lista (libro disattivato con
  // una nota), invece di scoprirlo solo dopo un tentativo fallito. Il fatto
  // che un altro membro abbia già pubblicato la sua non mi riguarda: resto
  // libero di pubblicare la mia. I libri EVENTO non hanno questo limite, non
  // vengono mai segnati. Un solo controllo all'apertura della schermata: se
  // pubblico proprio nel frattempo da un altro dispositivo, il trigger sul
  // database resta comunque la vera rete di sicurezza (vedi il messaggio
  // specifico più sotto).
  const [sharedPostedToday,setSharedPostedToday]=useState(new Set());
  useEffect(()=>{
    const nonEventoIds=openShared.filter(sb=>sb.kind!=="evento").map(sb=>sb.id);
    if(nonEventoIds.length===0||!userId){ setSharedPostedToday(new Set()); return; }
    let cancelled=false;
    fetchSharedBooksPostedToday(nonEventoIds,userId,DOY,YR).then(set=>{ if(!cancelled) setSharedPostedToday(set); });
    return ()=>{ cancelled=true; };
  },[st.sharedBooks,userId]);
  const activeFilter=st.filters.find(f=>f.id===filterId)||INIT_FILTERS[0];
  // "Originale" (nessun filtro) resta sempre la prima opzione, qualunque
  // sia il suo numero di utilizzi — altrimenti, con filtri più popolari,
  // rischia di sparire del tutto dalla riga rapida, senza modo di tornare
  // alla foto senza filtro se non sfogliando l'intera galleria.
  const noneFilter=st.filters.find(f=>f.id==="none")||INIT_FILTERS[0];
  const quickFilters=[noneFilter,...st.filters.filter(f=>f.id!=="none").slice().sort((a,b)=>b.uses-a.uses).slice(0,5)];
  const ownedStickers=REWARDS_CATALOG.filter(r=>r.type==="sticker"&&st.rewards.unlocked.includes(r.id));
  // Wrapper so the embedded gallery can close itself without unmounting Capture
  // (real navigation is only used for profile links, which legitimately leave the flow).
  const galleryGo=(target2)=>{
    if(target2&&target2.startsWith("profile_")){ setShowGallery(false); go(target2); }
    else setShowGallery(false);
  };
  const handleCreateMicro=async()=>{
    if(!newMicroTitle.trim()||creatingMicro)return;
    setCreatingMicro(true);
    const preset=MP[0]; // preset di base — il resto (copertina, colore) si personalizza dopo dalla schermata Micro-libri
    const created=userId?await createMicroBook(userId,{title:newMicroTitle.trim(),preset:preset.id,theme:st.myTheme||"neon"}):null;
    setCreatingMicro(false);
    if(created){
      dispatch({type:"ADD_MICRO",preset,title:newMicroTitle.trim(),theme:st.myTheme||"neon",id:created.id,real:true});
      setSaveMicroId(created.id);
      setShowNewMicro(false);
      setNewMicroTitle("");
    } else {
      pushToast(dispatch,tr("capture_toast_micro_create_failed"));
    }
  };
  const dragStart=(clientX,clientY)=>{
    dragState.current={active:true,startX:clientX,startY:clientY,startCropX:cropX,startCropY:cropY};
  };
  const dragMove=(clientX,clientY,boxSize)=>{
    if(!dragState.current.active)return;
    const dx=clientX-dragState.current.startX, dy=clientY-dragState.current.startY;
    // trascinare verso sinistra deve spostare il "punto visibile" verso destra: segno invertito
    const newX=dragState.current.startCropX-(dx/boxSize)*100;
    const newY=dragState.current.startCropY-(dy/boxSize)*100;
    setCropX(Math.max(0,Math.min(100,newX)));
    setCropY(Math.max(0,Math.min(100,newY)));
  };
  const dragEnd=()=>{ dragState.current.active=false; };
  // Apre la fotocamera vera del dispositivo — MA solo quando l'app gira
  // davvero come app nativa (dentro Capacitor). Sul sito oncer.app normale,
  // questo controllo è falso, e usiamo esattamente lo stesso metodo di
  // sempre (l'input del browser) — nessun rischio per chi usa il sito.
  const takePhoto=async()=>{
    if(!Capacitor.isNativePlatform()) return; // sul sito, ci pensa l'input qui sotto
    try{
      const image=await Camera.getPhoto({
        quality:85,
        allowEditing:false,
        resultType:CameraResultType.DataUrl,
        source:CameraSource.Camera,
      });
      if(image&&image.dataUrl) setPhoto(image.dataUrl);
    }catch(err){
      // L'utente ha annullato lo scatto, o ha negato il permesso alla
      // fotocamera — non è un errore vero da mostrare in modo invadente.
      if(err&&err.message&&!err.message.toLowerCase().includes("cancel")){
        console.error("Oncer: apertura fotocamera fallita —",err.message);
        pushToast(dispatch,tr("capture_toast_camera_failed"));
      }
    }
  };
  const handleWebFileInput=(e)=>{
    const file=e.target.files&&e.target.files[0];
    if(!file)return;
    const reader=new FileReader();
    reader.onload=ev=>{ setPhoto(ev.target.result); };
    reader.readAsDataURL(file);
  };
  if(todayDone&&step===0&&!microOnlyMode&&!eventLoopId) return(
    <div className="screen fade" style={{background:sectionGlowBg("capture",t),display:"flex",flexDirection:"column"}}>
      <div style={{padding:"14px",display:"flex",alignItems:"center",gap:11,borderBottom:`1px solid ${t.ac}18`,flexShrink:0}}>
        <span style={{fontSize:16,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("capture_title")}</span>
      </div>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",padding:30,gap:18,textAlign:"center"}}>
        <div style={{fontSize:60}}>✅</div>
        <div style={{fontSize:18,fontWeight:800,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("capture_already_done_title")}</div>
        <div style={{fontSize:13,color:t.soft,fontFamily:"Inter,sans-serif",lineHeight:1.65}}>{tr("capture_already_done_sub1",{day:DOY,year:YR})}<br/>{tr("capture_already_done_sub2")}</div>
        {(openMicro.length>0||openShared.length>0)&&(
          <Btn fn={()=>setMicroOnlyMode(true)} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"13px 28px",borderRadius:40,fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",display:"inline-block",marginTop:4}}>{tr("capture_shoot_for_micro")}</Btn>
        )}
        <Btn fn={()=>go("library")} style={{background:t.pill,color:"#fff",padding:"13px 28px",borderRadius:40,fontSize:14,fontWeight:700,fontFamily:"Inter,sans-serif",display:"inline-block"}}>{tr("capture_view_your_book")}</Btn>
      </div>
    </div>
  );
  if(step===0) return(
    <div className="screen" style={{background:"#06060E",display:"flex",flexDirection:"column"}}>
      <div style={{flex:1,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:20,padding:28}}>
        <div style={{fontSize:11,fontFamily:"Inter,sans-serif",color:t.ac,letterSpacing:"3px",textTransform:"uppercase"}}>
          {TODAY.toLocaleDateString(LOCALE,{day:"numeric",month:"long"})} · {tr("feed_page_abbr")} {DOY}
        </div>
        <style>{`
          @keyframes onceCamPulse {
            0%   { box-shadow: 0 0 0 0 ${t.ac}4d; }
            70%  { box-shadow: 0 0 0 18px ${t.ac}00; }
            100% { box-shadow: 0 0 0 0 ${t.ac}00; }
          }
        `}</style>
        {(()=>{
          // Contenuto del riquadro fotocamera: se una foto è già stata
          // scattata, la mostra; altrimenti, invece della vecchia emoji
          // sbiadita poco chiara, un'icona rotonda con un leggero effetto
          // pulsante e una scritta ben visibile — per far capire subito che
          // lì si tocca per aprire la fotocamera.
          // Riquadro rifatto il 31/08/2026 (richiesta di Matte, prototipato
          // prima in chat): via gli angoli ad HUD da fotocamera generica —
          // ora un quadrato vetro morbido, coerente col resto dell'app.
          // L'icona è la stessa fotocamera disegnata già usata nella barra
          // delle sezioni (NavIcon "capture"), non più l'emoji 📷. L'anello
          // che pulsa in continuo (onceCamPulse, già presente) resta e si
          // vede bene: è quello che fa capire che si può toccare.
          const finderInner=isPhotoUrl(photo)?(
            <img src={photo} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover"}}/>
          ):(
            <div style={{display:"flex",flexDirection:"column",alignItems:"center",gap:14,position:"relative",zIndex:1}}>
              <div style={{width:80,height:80,borderRadius:"50%",background:`${t.ac}26`,border:`1.5px solid ${t.ac}66`,display:"flex",alignItems:"center",justifyContent:"center",animation:"onceCamPulse 2.4s ease-out infinite"}}>
                <NavIcon type="capture" active={false} ac={t.ac} soft={t.ac} size={32}/>
              </div>
              <div style={{fontSize:14,color:"#fff",fontWeight:700,fontFamily:"Inter,sans-serif"}}>{tr("capture_tap_to_shoot")}</div>
            </div>
          );
          const boxStyle={width:256,height:256,borderRadius:28,border:`1px solid ${t.ac}55`,background:`linear-gradient(160deg,${t.ac}1c,${(t.ac2||t.ac)}10)`,backdropFilter:"blur(20px) saturate(190%)",WebkitBackdropFilter:"blur(20px) saturate(190%)",display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:10,boxShadow:`0 0 50px ${t.ac}22,inset 0 1.5px 0 rgba(255,255,255,.14)`,cursor:"pointer",position:"relative",overflow:"hidden"};
          return Capacitor.isNativePlatform()?(
            <div onClick={takePhoto} style={boxStyle}>
              {finderInner}
            </div>
          ):(
            <label style={boxStyle}>
              <input type="file" accept="image/*" capture="environment" style={{display:"none"}} onChange={handleWebFileInput}/>
              {finderInner}
            </label>
          );
        })()}
        <div style={{fontSize:12,fontFamily:"Inter,sans-serif",color:"rgba(255,255,255,.4)",textAlign:"center",lineHeight:1.7,maxWidth:260}}>{tr("capture_think_twice_1")}<br/><span style={{color:"rgba(255,255,255,.26)"}}>{tr("capture_think_twice_2")}</span></div>
        {photo==="📷"&&(
          // Vetro allineato al resto dell'app (31/08/2026): stesso identico
          // linguaggio di BNav/header Feed — tinto dell'accento del tema,
          // non più un bordo chiaro su fondo quasi trasparente che si
          // confondeva con lo sfondo scuro del mirino.
          <div onClick={()=>{setPhoto("✏️");setStep(1);}} style={{display:"flex",alignItems:"center",gap:9,padding:"13px 26px",borderRadius:40,background:t.ac+"26",backdropFilter:"blur(18px) saturate(190%)",WebkitBackdropFilter:"blur(18px) saturate(190%)",border:`1px solid ${t.ac}55`,boxShadow:"inset 0 1.5px 0 rgba(255,255,255,.14)",cursor:"pointer"}}>
            <span style={{fontSize:17}}>✏️</span>
            <span style={{fontSize:13.5,fontWeight:700,color:"#fff",fontFamily:"Inter,sans-serif"}}>{tr("capture_write_thought_only")}</span>
          </div>
        )}
      </div>
      {/* La ✕ per tornare al Feed è stata tolta il 31/08/2026 (richiesta di
          Matte): con la barra delle sezioni ora sempre fissa in basso
          (vedi App.jsx/Navigation.jsx), toccare "Feed" lì fa esattamente la
          stessa cosa — non serviva più una seconda via d'uscita qui, e la
          riga di controlli si semplifica a un solo pulsante centrato. */}
      <div style={{padding:"18px 28px 42px",display:"flex",justifyContent:"center",alignItems:"center",flexShrink:0}}>
        {/* Non è un secondo "scatta" — compare SOLO dopo che una foto è già
            stata presa, ed è il pulsante "conferma e continua" verso il
            passo successivo. */}
        <Btn fn={()=>photo!=="📷"&&setStep(1)} style={{width:72,height:72,borderRadius:"50%",background:photo!=="📷"?t.pill:"transparent",display:"flex",alignItems:"center",justifyContent:"center",fontSize:26,color:"#fff",boxShadow:photo!=="📷"?`0 0 0 6px ${t.ac}22,0 8px 24px ${t.ac}44`:"none",opacity:photo!=="📷"?1:0,pointerEvents:photo!=="📷"?"auto":"none",transition:"opacity .2s"}}>→</Btn>
      </div>
    </div>
  );
  return(
    <div className="screen fade" style={{background:t.bg,display:"flex",flexDirection:"column"}}>
      <div style={{background:t.pill,padding:"15px",display:"flex",alignItems:"center",gap:10,flexShrink:0}}>
        <div onClick={()=>{if(!saving)setStep(0);}} style={{width:32,height:32,borderRadius:"50%",background:"rgba(0,0,0,.2)",display:"flex",alignItems:"center",justifyContent:"center",cursor:saving?"default":"pointer",fontSize:14,color:"#fff",opacity:saving?.4:1}}>←</div>
        <span style={{fontSize:14,fontWeight:700,color:"#fff",fontFamily:"Inter,sans-serif",flex:1}}>{tr("capture_where_to_save")}</span>
      </div>
      <div style={{flex:1,overflowY:"auto",padding:15}}>
        {photo==="✏️"?(
          <>
            <div style={{display:"flex",justifyContent:"center",marginBottom:14}}>
              <div style={{width:220,minHeight:220,borderRadius:16,background:`linear-gradient(160deg,${t.ac}1c,${t.ac2||t.ac}0f)`,border:`1px solid ${t.ac}33`,display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",gap:12,padding:22,textAlign:"center",boxSizing:"border-box"}}>
                {caption.trim()
                  ?<span style={{...THOUGHT_FONTS[thoughtFont],fontFamily:THOUGHT_FONTS[thoughtFont].family,fontSize:17,lineHeight:1.5,color:t.tx}}>{caption}</span>
                  :<span style={{fontSize:40}}>✏️</span>}
                <span style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("feed_page_singular")} {DOY} · {tr("capture_words_only_suffix")}</span>
              </div>
            </div>
            <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:8}}>{tr("capture_which_font")}</div>
            <div style={{display:"flex",gap:8,marginBottom:14,flexWrap:"wrap"}}>
              {Object.entries(THOUGHT_FONTS).map(([key,f])=>(
                <div key={key} onClick={()=>setThoughtFont(key)} style={{padding:"9px 14px",borderRadius:12,cursor:"pointer",background:thoughtFont===key?t.pill:t.card,border:thoughtFont===key?"none":`1px solid ${t.bord}`}}>
                  <div style={{fontFamily:f.family,fontStyle:f.style,fontWeight:f.weight,fontSize:15,color:thoughtFont===key?"#fff":t.tx,lineHeight:1}}>Aa</div>
                  <div style={{fontSize:8.5,color:thoughtFont===key?"rgba(255,255,255,.8)":t.soft,fontFamily:"Inter,sans-serif",marginTop:3}}>{f.label}</div>
                </div>
              ))}
            </div>
          </>
        ):(
          <div style={{display:"flex",flexDirection:"column",alignItems:"center",marginBottom:14}}>
            <div
              style={{width:220,height:220,position:"relative",borderRadius:16,overflow:"hidden",touchAction:"none",cursor:"grab",userSelect:"none"}}
              onPointerDown={e=>{
                e.preventDefault();
                e.currentTarget.setPointerCapture(e.pointerId);
                dragStart(e.clientX,e.clientY);
              }}
              onPointerMove={e=>{
                if(!dragState.current.active)return;
                e.preventDefault();
                dragMove(e.clientX,e.clientY,220);
              }}
              onPointerUp={e=>{
                dragEnd();
                try{ e.currentTarget.releasePointerCapture(e.pointerId); }catch(err){}
              }}
              onPointerCancel={dragEnd}
            >
              {isPhotoUrl(photo)
                ? <img src={photo} draggable={false} style={{position:"absolute",inset:0,width:"100%",height:"100%",objectFit:"cover",objectPosition:`${cropX}% ${cropY}%`,filter:activeFilter&&activeFilter.css&&activeFilter.css!=="none"?activeFilter.css:"none",pointerEvents:"none"}}/>
                : <FilterTile filter={activeFilter} emoji={photo} size={220}/>}
              {stickerId&&<div style={{position:"absolute",bottom:8,right:8,fontSize:34,filter:"drop-shadow(0 2px 4px rgba(0,0,0,.4))"}}>{REWARDS_CATALOG.find(r=>r.id===stickerId)?.emoji}</div>}
            </div>
            {isPhotoUrl(photo)&&<div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",marginTop:7}}>{tr("capture_drag_to_reframe")}</div>}
          </div>
        )}
        {photo!=="✏️"&&<div style={{marginBottom:14}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",marginBottom:9}}>
            <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase"}}>{tr("capture_filter_label")}</div>
            <span onClick={()=>setShowGallery(true)} style={{fontSize:11,color:t.ac,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("capture_browse_all")}</span>
          </div>
          <div style={{display:"flex",gap:9,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
            {quickFilters.map(f=>(
              <div key={f.id} onClick={()=>setFilterId(f.id)} style={{flexShrink:0,textAlign:"center",cursor:"pointer"}}>
                <div style={{position:"relative"}}>
                  <FilterTile filter={f} emoji={photo} size={56}/>
                  {filterId===f.id&&<div style={{position:"absolute",inset:-3,border:`2px solid ${t.ac}`,borderRadius:16,pointerEvents:"none"}}/>}
                </div>
                <div style={{fontSize:8.5,color:filterId===f.id?t.ac:t.soft,fontFamily:"Inter,sans-serif",fontWeight:filterId===f.id?700:400,marginTop:4,maxWidth:56,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap"}}>{f.name}</div>
              </div>
            ))}
          </div>
        </div>}
        {photo!=="✏️"&&ownedStickers.length>0&&(
          <div style={{marginBottom:14}}>
            <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:9}}>{tr("capture_sticker_label")}</div>
            <div style={{display:"flex",gap:9,overflowX:"auto",scrollbarWidth:"none",paddingBottom:2}}>
              <div onClick={()=>setStickerId(null)} style={{flexShrink:0,width:44,height:44,borderRadius:12,background:t.card,border:!stickerId?`2px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:16,color:t.soft,cursor:"pointer"}}>✕</div>
              {ownedStickers.map(s=>(
                <div key={s.id} onClick={()=>setStickerId(s.id)} style={{flexShrink:0,width:44,height:44,borderRadius:12,background:t.card,border:stickerId===s.id?`2px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",justifyContent:"center",fontSize:22,cursor:"pointer"}}>{s.emoji}</div>
              ))}
            </div>
          </div>
        )}
        <div style={{marginBottom:12}}>
          {microOnlyMode?(
            <>
              <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:8}}>{tr("capture_annual_book_label")}</div>
              {todayDone?(
                <div style={{background:t.card,borderRadius:12,padding:"11px 13px",border:`1px solid ${t.bord}`,display:"flex",alignItems:"center",gap:10}}>
                  <div style={{width:36,height:36,borderRadius:9,background:t.soft+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>✓</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("capture_already_done_today")}</div>
                    <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("capture_only_micro_note")}</div>
                  </div>
                </div>
              ):(
                <div onClick={()=>setAddToDiaryToo(v=>!v)} style={{background:t.card,borderRadius:12,padding:"11px 13px",border:addToDiaryToo?`1.5px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <div style={{width:36,height:36,borderRadius:9,background:addToDiaryToo?t.pill:t.soft+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,color:addToDiaryToo?"#fff":t.soft}}>{addToDiaryToo?"✓":""}</div>
                  <div style={{flex:1}}>
                    <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("capture_also_diary_label")}</div>
                    <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("capture_also_diary_hint")}</div>
                  </div>
                </div>
              )}
            </>
          ):(
            <>
              <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:8}}>{tr("capture_always_in_book")}</div>
              <div style={{background:t.card,borderRadius:12,padding:"11px 13px",border:`1.5px solid ${t.ac}`,display:"flex",alignItems:"center",gap:10}}>
                <div style={{width:36,height:36,borderRadius:9,background:t.pill,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{TH[Object.keys(TH)[0]].emoji}</div>
                <div style={{flex:1}}>
                  <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>{tr("capture_annual_book_year",{year:YR})}</div>
                  <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>{tr("feed_page_singular")} {DOY} · {tr("capture_permanent")}</div>
                </div>
                <span style={{color:t.ac,fontSize:18}}>✓</span>
              </div>
            </>
          )}
          <>
            <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",margin:"14px 0 8px"}}>{microOnlyMode?tr("capture_choose_micro_required"):tr("capture_add_to_micro_optional")}</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {openMicro.map(mb=>{
                const grad=(MP.find(x=>x.id===mb.preset)||MP[0]).grad;
                const active=String(saveMicroId)===String(mb.id);
                return(
                  <div key={mb.id} onClick={()=>setSaveMicroId(active?null:mb.id)} style={{background:t.card,borderRadius:12,padding:"11px 13px",border:active?`1.5px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                    <div style={{width:36,height:36,borderRadius:9,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{mb.cover}</div>
                    <div style={{flex:1}}>
                      <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>{mb.title}</div>
                      <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>📎 {tr("capture_micro_book_word")} · {tr("feed_page_abbr")} {mb.pages+1}</div>
                    </div>
                    {active&&<span style={{color:t.ac,fontSize:18}}>✓</span>}
                  </div>
                );
              })}
              {!showNewMicro?(
                <div onClick={()=>setShowNewMicro(true)} style={{background:t.card,borderRadius:12,padding:"11px 13px",border:`1px dashed ${t.bord}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer"}}>
                  <div style={{width:36,height:36,borderRadius:9,background:t.soft+"18",display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0,color:t.ac,fontWeight:700}}>+</div>
                  <div style={{fontSize:13,fontWeight:700,color:t.ac,fontFamily:"Inter,sans-serif"}}>{tr("capture_create_new_micro")}</div>
                </div>
              ):(
                <div style={{background:t.card,borderRadius:12,padding:"11px 13px",border:`1.5px solid ${t.ac}`}}>
                  <input value={newMicroTitle} onChange={e=>setNewMicroTitle(e.target.value)} onKeyDown={e=>{if(e.key==="Enter")handleCreateMicro();}} placeholder={tr("capture_micro_title_placeholder")} autoFocus maxLength={40} style={{width:"100%",border:"none",background:"transparent",fontSize:14,fontFamily:"Inter,sans-serif",fontWeight:600,color:t.tx,marginBottom:8}}/>
                  <div style={{display:"flex",gap:8}}>
                    <div onClick={()=>{setShowNewMicro(false);setNewMicroTitle("");}} style={{flex:1,textAlign:"center",padding:"8px",borderRadius:9,background:t.soft+"14",fontSize:12,fontWeight:600,color:t.soft,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("capture_cancel")}</div>
                    <div onClick={handleCreateMicro} style={{flex:1,textAlign:"center",padding:"8px",borderRadius:9,background:newMicroTitle.trim()?t.pill:t.soft+"22",fontSize:12,fontWeight:700,color:"#fff",fontFamily:"Inter,sans-serif",cursor:"pointer",opacity:creatingMicro?.6:1}}>{creatingMicro?tr("capture_creating"):tr("capture_create")}</div>
                  </div>
                </div>
              )}
            </div>
          </>
          {openShared.length>0&&(
            <>
              <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",margin:"14px 0 8px"}}>{tr("capture_add_to_shared_optional")}</div>
              <div style={{display:"flex",flexDirection:"column",gap:8}}>
                {openShared.map(sb=>{
                  const grad=(MP.find(x=>x.id===sb.preset)||MP[0]).grad;
                  const active=String(saveSharedId)===String(sb.id);
                  // Libri "family"/"a tempo" (non EVENTO) ammettono una sola
                  // foto al giorno per l'intero libro — se qualcuno l'ha già
                  // pubblicata oggi, il libro si mostra disattivato con una
                  // nota, invece di lasciar provare per poi fallire.
                  const blockedToday=sb.kind!=="evento"&&sharedPostedToday.has(String(sb.id));
                  return(
                    <div key={sb.id} onClick={()=>{
                      if(blockedToday){ pushToast(dispatch,tr("capture_toast_shared_already_today")); return; }
                      setSaveSharedId(active?null:sb.id);
                    }} style={{background:t.card,borderRadius:12,padding:"11px 13px",border:active?`1.5px solid ${t.ac}`:`1px solid ${t.bord}`,display:"flex",alignItems:"center",gap:10,cursor:"pointer",opacity:blockedToday?.5:1}}>
                      <div style={{width:36,height:36,borderRadius:9,background:grad,display:"flex",alignItems:"center",justifyContent:"center",fontSize:17,flexShrink:0}}>{sb.cover}</div>
                      <div style={{flex:1}}>
                        <div style={{fontSize:13,fontWeight:700,color:t.tx,fontFamily:"Inter,sans-serif"}}>{sb.title} {sb.kind==="family"?"👨‍👩‍👧‍👦":sb.kind==="evento"?"💍":"🤝"}</div>
                        <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif"}}>{blockedToday?tr("capture_toast_shared_already_today"):sb.kind==="evento"?tr("capture_event_book_note"):`${tr("capture_with_prefix")} ${(sb.members||[]).filter(m=>m!==(st.profile.username||"tu")).join(", ")||"..."}`}</div>
                      </div>
                      {active&&!blockedToday&&<span style={{color:t.ac,fontSize:18}}>✓</span>}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
        <div style={{background:t.card,borderRadius:13,padding:13,border:photo==="✏️"?`1.5px solid ${t.ac}`:`1px solid ${t.bord}`,marginBottom:11}}>
          <div style={{fontSize:10,color:t.soft,fontFamily:"Inter,sans-serif",letterSpacing:"1px",textTransform:"uppercase",marginBottom:7}}>{photo==="✏️"?tr("capture_your_thought_today"):tr("capture_add_a_caption")}</div>
          <textarea value={caption} onChange={e=>setCaption(e.target.value)} placeholder={photo==="✏️"?tr("capture_thought_placeholder"):tr("capture_caption_placeholder")} maxLength={photo==="✏️"?500:140} rows={photo==="✏️"?6:2} autoFocus={photo==="✏️"} style={{width:"100%",border:"none",background:"transparent",fontSize:16,fontFamily:"Inter,sans-serif",color:t.tx,resize:"none",lineHeight:1.5,boxSizing:"border-box"}}/>
          <div style={{fontSize:9,color:t.soft,fontFamily:"Inter,sans-serif",textAlign:"right",marginTop:2}}>{caption.length}/{photo==="✏️"?500:140}</div>
        </div>
        <div style={{padding:"10px 13px",borderRadius:11,background:t.ac+"14",fontSize:11,fontFamily:"Inter,sans-serif",color:t.ac,lineHeight:1.55}}>{photo==="✏️"?tr("capture_thought_not_removable"):tr("capture_photo_not_removable")}</div>
      </div>
      <div style={{padding:"12px 14px 26px",flexShrink:0}}>
        <Btn full fn={async()=>{
          if(saving)return;
          if(photo==="✏️"&&!caption.trim())return;
          if(microOnlyMode&&!saveMicroId&&!saveSharedId){
            pushToast(dispatch,tr("capture_toast_choose_micro_first"));
            return;
          }
          // Vuole davvero toccare il diario personale? Sempre sì nel flusso
          // normale (tab "Scatta"); in modalità "solo micro-libro" solo se
          // l'utente ha attivato esplicitamente il toggle qui sopra.
          const wantsDiary=!microOnlyMode||addToDiaryToo;
          // Controllo fresco, non quello calcolato al montaggio del componente:
          // se un salvataggio precedente in questa stessa sessione è già
          // andato a buon fine (es. dopo un "indietro" molto rapido), non
          // tentare un secondo inserimento per lo stesso giorno — mostra
          // subito un messaggio chiaro invece di far fallire il database.
          // Se questa foto non deve toccare il diario (solo micro-libro,
          // toggle spento), il fatto che oggi sia già stato fatto non conta:
          // si procede comunque, è previsto.
          const alreadyDone=st.books[YR]?.pages?.some(p=>p.day===DOY);
          if(alreadyDone&&wantsDiary){
            pushToast(dispatch,tr("capture_toast_already_today_reload"));
            return;
          }
          setSaving(true);
          // Controllo di moderazione, solo per foto vere (non per i pensieri
          // scritti) — questo resta bloccante: deve avvenire PRIMA che la
          // foto compaia ovunque, altrimenti rischi di mostrarla un attimo e
          // poi doverla ritirare. Non blocca se il servizio ha un problema
          // tecnico (meglio lasciar passare che rompere l'esperienza per un
          // errore nostro).
          if(userId&&isPhotoUrl(photo)&&photo.startsWith("data:")){
            try{
              const {data:modData,error:modErr}=await supa.functions.invoke("moderate-content",{body:{type:"image",content:photo,userId}});
              if(!modErr&&modData&&modData.flagged){
                setSaving(false);
                setBlockedInfo({kind:tr("capture_kind_photo")});
                return;
              }
            }catch(modCallErr){
              console.error("Oncer: controllo di moderazione fallito, si procede comunque —",modCallErr.message);
            }
          }
          // Da qui in poi: il ritaglio è locale e veloce (nessuna rete), lo
          // aspettiamo per mostrare subito la foto già inquadrata come
          // scelta dall'utente. L'upload vero su Storage e i salvataggi sul
          // database, che possono richiedere qualche secondo, partono dopo
          // ma NON vengono aspettati — la pagina compare già completa e
          // l'utente può continuare a usare l'app nel frattempo.
          let finalPhotoLocal=photo==="✏️"?`✏️:${thoughtFont}`:photo;
          let compressedBlob=null;
          if(userId&&isPhotoUrl(photo)&&photo.startsWith("data:")){
            try{
              compressedBlob=await cropImageToSquare(photo,cropX,cropY);
              finalPhotoLocal=await blobToDataUrl(compressedBlob);
            }catch(compressErr){
              console.error("Oncer: ritaglio foto fallito, uso l'originale —",compressErr.message);
            }
          }
          const pageTs=Date.now();
          dispatch({type:"ADD_PAGE",photo:finalPhotoLocal,caption,saveMicroId,day:DOY,year:YR,filterId,stickerId,toDiary:wantsDiary,pageTs});
          setSaving(false);
          setJustPosted({photo:finalPhotoLocal,caption,day:DOY,user:st.profile.username||"tu",filter:filterId,sharedBookId:saveSharedId});
          if(userId){
            // Job in background: continua anche se l'utente lascia questa
            // schermata, perché dispatch/pushToast agiscono sullo stato
            // globale dell'app, non su questo componente — l'unica
            // condizione è che l'app resti aperta (anche in background),
            // non chiusa del tutto, per tutta la sua breve durata.
            (async()=>{
              let finalPhoto=finalPhotoLocal;
              if(compressedBlob){
                try{
                  finalPhoto=await uploadPhotoToStorage(compressedBlob,userId);
                  if(wantsDiary) dispatch({type:"UPDATE_PAGE_PHOTO",year:YR,day:DOY,feedId:pageTs,photo:finalPhoto});
                }catch(uploadErr){
                  console.error("Oncer: upload su Storage fallito, uso la versione compressa in locale —",uploadErr.message);
                  pushToast(dispatch,tr("capture_toast_saved_local_not_uploaded"));
                }
              }
              let microBookId=null;
              if(saveMicroId){
                const targetBook=st.micro.find(m=>String(m.id)===String(saveMicroId));
                if(targetBook&&targetBook.real) microBookId=targetBook.id;
              }
              const jobs=[];
              // Riga del diario personale (micro_book_id nullo) — solo se
              // questa foto deve davvero contare come pagina di oggi.
              if(wantsDiary){
                jobs.push(supa.from("pages").insert({
                  user_id:userId,year:YR,day:DOY,photo:finalPhoto,caption:caption||null,
                  filter_id:filterId||"none",sticker_id:stickerId||null,
                  micro_book_id:null,
                }).then(({error})=>{
                  if(error){
                    console.error("Oncer: pagina diario salvata solo in locale, sync su database fallito —",error.message);
                    const isDuplicateDay=error.code==="23505"||(error.message&&error.message.toLowerCase().includes("duplicate"));
                    pushToast(dispatch,isDuplicateDay?tr("capture_toast_already_today_local_only"):tr("capture_toast_saved_local_not_synced"));
                  }
                }));
              }
              // Riga separata per il micro-libro personale — indipendente dal
              // diario, può essercene più di una nello stesso giorno.
              if(microBookId){
                jobs.push(supa.from("pages").insert({
                  user_id:userId,year:YR,day:DOY,photo:finalPhoto,caption:caption||null,
                  filter_id:filterId||"none",sticker_id:stickerId||null,
                  micro_book_id:microBookId,
                }).then(({error:microErr})=>{
                  if(microErr){
                    console.error("Oncer: pagina micro-libro salvata solo in locale, sync su database fallito —",microErr.message);
                    pushToast(dispatch,tr("capture_toast_micro_not_synced"));
                  }
                }));
              }
              if(saveSharedId){
                // Il controllo fatto all'apertura della schermata (vedi
                // sharedPostedToday più sopra) copre il caso comune, ma tra
                // l'apertura e questo esatto istante un altro membro può
                // comunque aver pubblicato la sua — per questo distinguiamo
                // ancora qui "già pubblicata oggi da qualcun altro" (previsto,
                // messaggio chiaro) da un vero errore tecnico.
                jobs.push(postToSharedBook(saveSharedId,userId,{photo:finalPhoto,caption,day:DOY,year:YR}).then(result=>{
                  if(result==="already_today") pushToast(dispatch,tr("capture_toast_shared_already_today"));
                  else if(!result) pushToast(dispatch,tr("capture_toast_shared_publish_failed"));
                }));
              }
              await Promise.all(jobs);
            })();
          }
        }} style={{background:t.pill,color:"#fff",padding:"15px",borderRadius:40,fontSize:15,fontWeight:700,fontFamily:"Inter,sans-serif",textAlign:"center",boxShadow:`0 7px 22px ${t.ac}44`,opacity:saving?.7:(photo==="✏️"&&!caption.trim())?.4:(microOnlyMode&&!saveMicroId&&!saveSharedId)?.4:1}}>
          {saving?tr("capture_saving"):(photo==="✏️"&&!caption.trim())?tr("capture_write_something_first"):(microOnlyMode&&!saveMicroId&&!saveSharedId)?tr("capture_choose_micro_before_save"):microOnlyMode?tr("capture_save_in_micro"):tr("capture_save_in_page",{day:DOY})}
        </Btn>
      </div>
      {justPosted&&(()=>{
        // Se la foto è appena stata pubblicata in un libro EVENTO, offriamo
        // subito la scorciatoia per aggiungerne un'altra, saltando tutta la
        // schermata "hai già pubblicato oggi" — un ricevimento genera tante
        // foto nello stesso giorno, non una sola.
        const usedEventBook=justPosted.sharedBookId?openShared.find(sb=>String(sb.id)===String(justPosted.sharedBookId)&&sb.kind==="evento"):null;
        return(
          <div className="overlay">
            <div className="sheet" style={{textAlign:"center",padding:"38px 24px"}}>
              <div style={{fontSize:50,marginBottom:14}}>🎉</div>
              <div style={{fontSize:18,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif",marginBottom:8}}>{tr("capture_page_published",{day:justPosted.day})}</div>
              <div style={{fontSize:12,color:"#888",fontFamily:"Inter,sans-serif",marginBottom:22,lineHeight:1.6}}>{tr("capture_tell_world")}</div>
              {usedEventBook&&(
                <Btn fn={()=>{
                  setEventLoopId(usedEventBook.id);
                  setSaveSharedId(usedEventBook.id);
                  setMicroOnlyMode(true);
                  setAddToDiaryToo(false);
                  setPhoto("📷");
                  setCaption("");
                  setStickerId(null);
                  setFilterId("none");
                  setJustPosted(null);
                  setStep(0);
                }} style={{background:t.pill,color:"#fff",padding:"13px 26px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",display:"block",marginBottom:10}}>{tr("capture_add_another_to_event")}</Btn>
              )}
              <Btn fn={()=>setShowShareAfter(true)} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"13px 26px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",display:"block",marginBottom:10}}>{tr("capture_share_now")}</Btn>
              <Btn fn={()=>{setEventLoopId(null);setJustPosted(null);go("feed");}} style={{background:"transparent",color:"#999",padding:"10px 26px",borderRadius:40,fontSize:12.5,fontWeight:600,fontFamily:"Inter,sans-serif",display:"block"}}>{tr("capture_continue_in_app")}</Btn>
            </div>
          </div>
        );
      })()}
      {showShareAfter&&justPosted&&(
        <ShareSheet item={justPosted} filter={st.filters.find(f=>f.id===justPosted.filter)} ut={t} onClose={()=>{setShowShareAfter(false);setJustPosted(null);go("feed");}} dispatch={dispatch}/>
      )}
      {showGallery&&(
        <FiltersView t={t} st={st} dispatch={dispatch} go={galleryGo} pickMode={true} onPickFilter={(id)=>{setFilterId(id);setShowGallery(false);}}/>
      )}
      {blockedInfo&&(
        <div className="overlay" onClick={()=>setBlockedInfo(null)}>
          <div className="sheet" onClick={e=>e.stopPropagation()} style={{textAlign:"center",padding:"32px 24px"}}>
            <div style={{fontSize:44,marginBottom:14}}>🚫</div>
            <div style={{fontSize:16,fontWeight:800,color:"#1a1a1a",fontFamily:"Inter,sans-serif",marginBottom:8}}>{tr("capture_blocked_title",{kind:blockedInfo.kind})}</div>
            <div style={{fontSize:12.5,color:"#888",fontFamily:"Inter,sans-serif",marginBottom:22,lineHeight:1.6}}>{tr("capture_blocked_desc")}</div>
            <div onClick={()=>{window.location.href=`mailto:hello@oncer.app?subject=${encodeURIComponent(tr("capture_blocked_email_subject"))}`;}} style={{background:"linear-gradient(135deg,#FF2D6B,#7B2FFF)",color:"#fff",padding:"13px 26px",borderRadius:40,fontSize:13,fontWeight:700,fontFamily:"Inter,sans-serif",cursor:"pointer",marginBottom:10}}>{tr("capture_write_to_us")}</div>
            <div onClick={()=>setBlockedInfo(null)} style={{color:"#999",padding:"10px",fontSize:12.5,fontWeight:600,fontFamily:"Inter,sans-serif",cursor:"pointer"}}>{tr("capture_got_it")}</div>
          </div>
        </div>
      )}
    </div>
  );
}
